import { BigNumber } from '@ethersproject/bignumber'
import { Contract } from '@ethersproject/contracts'
import { formatUnits } from '@ethersproject/units'
import { useCallback, useEffect, useMemo, useState } from 'react'

import MULTI_REWARD_ABI from '../constants/abis/farms-multi-reward.json'
import { getProvider, PUBLIC_RPCS } from '../data/stats/useMultiChainProvider'
import { FARM_ADDRESSES } from '../data/sunset/types'
import { useActiveWeb3React } from './index'

// Minimal ERC20 ABI for fetching token info
const ERC20_ABI = [
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)'
]

export interface UnclaimedReward {
  tokenAddress: string
  tokenSymbol: string
  amount: BigNumber
  amountFormatted: number
  decimals: number
}

export interface FarmPosition {
  farmAddress: string
  stakedBalance: BigNumber
  chainId: number
  stakingToken: string // LP pair address
  unclaimedRewards: UnclaimedReward[]
}

export interface FarmUnstakeState {
  status: 'idle' | 'pending' | 'success' | 'error'
  txHash?: string
  error?: string
}

// Get a farm contract instance (for signing transactions - requires connected wallet)
function useFarmContract(farmAddress: string | undefined): Contract | null {
  const { library, account } = useActiveWeb3React()

  return useMemo(() => {
    if (!farmAddress || !library || !account) return null
    try {
      return new Contract(farmAddress, MULTI_REWARD_ABI, library.getSigner(account))
    } catch {
      return null
    }
  }, [farmAddress, library, account])
}

// Farm networks (Polygon and BSC only)
const FARM_CHAIN_IDS = [137, 56]

/**
 * Hook to check user's farm stake across ALL farm networks.
 * Uses public RPC endpoints to query each chain independently.
 * Returns an array of all positions found.
 */
export function useFarmPosition(): {
  loading: boolean
  positions: FarmPosition[]
  // Legacy single position (first found) for backward compatibility
  position: FarmPosition | null
  refresh: () => void
} {
  const { account } = useActiveWeb3React()
  const [loading, setLoading] = useState(false)
  const [positions, setPositions] = useState<FarmPosition[]>([])

  const refresh = useCallback(async () => {
    if (!account) {
      setPositions([])
      return
    }

    setLoading(true)
    try {
      // Query all farm networks in parallel
      const chainPromises = FARM_CHAIN_IDS.map(
        async (chainId): Promise<FarmPosition | null> => {
          const farmAddress = FARM_ADDRESSES[chainId]
          if (!farmAddress) return null

          // Skip chains we don't have RPC for
          if (!PUBLIC_RPCS[chainId]) return null

          const provider = getProvider(chainId)
          if (!provider) return null

          try {
            const farmContract = new Contract(farmAddress, MULTI_REWARD_ABI, provider)

            const [balance, stakingToken, rewardTokenAddresses]: [
              BigNumber,
              string,
              string[]
            ] = await Promise.all([
              farmContract.balanceOf(account),
              farmContract.stakingToken(),
              farmContract.getAllRewardTokens()
            ])

            if (balance.isZero()) return null

            // Fetch unclaimed rewards for each reward token
            const unclaimedRewards: UnclaimedReward[] = []

            for (const rewardTokenAddress of rewardTokenAddresses) {
              try {
                const tokenContract = new Contract(rewardTokenAddress, ERC20_ABI, provider)
                const [symbol, decimals, earned] = await Promise.all([
                  tokenContract.symbol(),
                  tokenContract.decimals(),
                  farmContract.earned(account, rewardTokenAddress)
                ])

                if (earned.gt(0)) {
                  unclaimedRewards.push({
                    tokenAddress: rewardTokenAddress,
                    tokenSymbol: symbol,
                    amount: earned,
                    amountFormatted: parseFloat(formatUnits(earned, decimals)),
                    decimals
                  })
                }
              } catch (err) {
                console.warn(
                  `Failed to fetch reward token ${rewardTokenAddress} on chain ${chainId}:`,
                  err
                )
              }
            }

            return {
              farmAddress,
              stakedBalance: balance,
              chainId,
              stakingToken,
              unclaimedRewards
            }
          } catch (error) {
            console.warn(`Error fetching farm position on chain ${chainId}:`, error)
            return null
          }
        }
      )

      const results = await Promise.all(chainPromises)
      const validPositions = results.filter((p): p is FarmPosition => p !== null)

      setPositions(validPositions)
    } catch (error) {
      console.error('Error fetching farm positions:', error)
      setPositions([])
    } finally {
      setLoading(false)
    }
  }, [account])

  useEffect(() => {
    refresh()
  }, [refresh])

  // Legacy: return first position for backward compatibility
  const position = positions.length > 0 ? positions[0] : null

  return { loading, positions, position, refresh }
}

// Hook to unstake from farm (exit = withdraw all + claim rewards)
// This still requires connected wallet on the correct chain
export function useFarmUnstake(farmAddress: string | undefined) {
  const farmContract = useFarmContract(farmAddress)

  const [state, setState] = useState<FarmUnstakeState>({ status: 'idle' })

  const exit = useCallback(async () => {
    if (!farmContract) {
      setState({ status: 'error', error: 'Farm contract not available' })
      return
    }

    try {
      setState({ status: 'pending' })

      // Call exit() which withdraws all staked LP and claims all rewards
      const tx = await farmContract.exit({
        gasLimit: BigNumber.from(500000)
      })

      const receipt = await tx.wait()

      setState({ status: 'success', txHash: receipt.transactionHash })
    } catch (err) {
      const error = err as { code?: number | string; message?: string }
      console.error('Farm unstake error:', error)
      if (error?.code === 4001 || error?.code === 'ACTION_REJECTED') {
        setState({ status: 'idle' })
      } else {
        setState({ status: 'error', error: error?.message || 'Unstake failed' })
      }
    }
  }, [farmContract])

  const reset = useCallback(() => {
    setState({ status: 'idle' })
  }, [])

  return useMemo(
    () => ({
      exit,
      reset,
      ...state
    }),
    [exit, reset, state]
  )
}

// Format staked balance for display
export function formatStakedBalance(balance: BigNumber): string {
  // LP tokens typically have 18 decimals
  const formatted = Number(balance.toString()) / 1e18
  if (formatted < 0.000001) return '< 0.000001'
  if (formatted < 1) return formatted.toFixed(6)
  return formatted.toFixed(4)
}
